import { useEffect, useState, type FormEvent } from 'react';
import { Megaphone, Users } from 'lucide-react';
import { Button, Input, Notice, Select } from '../ui';
import { Drawer } from '../ui/Drawer';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';

interface Modelo { id: string; name: string; language: string; category: string; status: string; wabaAccount: { wabaId: string } }
interface Canal { id: string; wabaId: string; phoneNumbers: Array<{ id: string; displayNumber: string }> }
interface Tag { id: string; name: string }

/** `datetime-local` no formato que o input exige, em horário LOCAL. `toISOString`
 *  devolveria UTC e o campo mostraria a hora errada por três horas. */
function paraCampo(data: Date): string {
  const local = new Date(data.getTime() - data.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Composição de disparo.
 *
 * `POST /campaigns` e `POST /campaigns/preview` existiam desde sempre sem
 * nenhuma tela: dava para cancelar um disparo pela interface, nunca para criar
 * um. O `preview` — que devolve quantos contatos o segmento atinge e se passou
 * do teto — só faz sentido dentro de um formulário como este, o que indica que
 * ele foi escrito para uma tela que nunca chegou.
 *
 * Quatro regras do servidor que aparecem aqui como restrição visível, e não
 * como 422 depois de preencher tudo:
 *
 * 1. O modelo precisa pertencer à mesma conta WABA do número escolhido. A
 *    lista de modelos é filtrada pelo número; sem isso, a combinação errada só
 *    seria descoberta no envio.
 * 2. Só modelo APROVADO. Os demais nem entram na lista.
 * 3. Só contatos com opt-in promocional entram no alcance — por isso o número
 *    do preview quase nunca é igual ao total de contatos, e a tela diz isso.
 * 4. Teto de 500 destinatários, marcado como "deste MVP" no código do servidor.
 *    O aviso nomeia o limite em vez de apenas recusar.
 */
export function FormularioDisparo({
  onFechar, onCriado,
}: { onFechar: () => void; onCriado: (mensagem: string) => void }) {
  const canais = useResource<Canal[]>('/workspace/channels', { vazio: () => false });
  const modelos = useResource<Modelo[]>('/workspace/templates', { vazio: () => false });
  const tags = useResource<Tag[]>('/workspace/tags', { vazio: () => false });

  const [numero, setNumero] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [modelo, setModelo] = useState('');
  const [alcance, setAlcance] = useState<{ recipients: number; limitExceeded: boolean } | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const listaCanais = canais.state.status === 'ready' ? canais.state.data : [];
  const listaTags = tags.state.status === 'ready' ? tags.state.data : [];
  const listaModelos = modelos.state.status === 'ready' ? modelos.state.data : [];

  const numeros = listaCanais.flatMap((c) => c.phoneNumbers.map((p) => ({ ...p, wabaId: c.wabaId })));
  const wabaDoNumero = numeros.find((n) => n.id === numero)?.wabaId;
  const modelosCompativeis = listaModelos.filter(
    (m) => m.status === 'APPROVED' && (!wabaDoNumero || m.wabaAccount.wabaId === wabaDoNumero),
  );

  // Trocar o número pode invalidar o modelo já escolhido — ele pertence a outra
  // conta WABA. Limpar é melhor do que deixar uma seleção que o servidor
  // recusaria no envio.
  useEffect(() => {
    if (modelo && !modelosCompativeis.some((m) => m.id === modelo)) setModelo('');
  }, [numero, modelo, modelosCompativeis]);

  /** O alcance é consultado quando número ou segmento mudam: é a informação
   *  que decide se vale disparar, e escondê-la atrás de um botão faz a pessoa
   *  agendar às cegas. */
  useEffect(() => {
    if (!numero) { setAlcance(null); return; }
    let vivo = true;
    setCalculando(true);
    api<{ recipients: number; limitExceeded: boolean }>('/workspace/campaigns/preview', {
      method: 'POST',
      body: { phoneNumberId: numero, ...(etiqueta ? { tagId: etiqueta } : {}) },
    })
      .then((r) => { if (vivo) setAlcance(r); })
      .catch(() => { if (vivo) setAlcance(null); })
      .finally(() => { if (vivo) setCalculando(false); });
    return () => { vivo = false; };
  }, [numero, etiqueta]);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    const quando = String(dados.get('scheduledAt') ?? '');
    setErro(null);
    if (!quando) { setErro('Escolha a data e a hora do envio.'); return; }
    setSalvando(true);
    try {
      const r = await api<{ recipients: number }>('/workspace/campaigns', {
        method: 'POST',
        body: {
          name: String(dados.get('name') ?? '').trim(),
          phoneNumberId: numero,
          ...(etiqueta ? { tagId: etiqueta } : {}),
          templateId: modelo,
          scheduledAt: new Date(quando).toISOString(),
        },
      });
      onCriado(`Disparo agendado para ${r.recipients} ${r.recipients === 1 ? 'contato' : 'contatos'}.`);
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  const minimo = paraCampo(new Date(Date.now() + 5 * 60_000));

  return (
    <Drawer
      open
      onClose={onFechar}
      title="Novo disparo"
      description="Envia um modelo aprovado para os contatos de um segmento que aceitaram mensagens promocionais."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary" type="submit" form="form-disparo"
            loading={salvando} loadingLabel="Agendando o disparo"
            icon={<Megaphone size={18} aria-hidden="true" />}
            disabled={!numero || !modelo || alcance?.recipients === 0}
          >
            Agendar disparo
          </Button>
        </>
      }
    >
      <form id="form-disparo" onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-5)' }}>
        {erro && <Notice tone="danger" title="Não foi possível agendar" live>{erro}</Notice>}

        <Input label="Nome do disparo" name="name" required minLength={2} maxLength={120}
          placeholder="Lembrete de agosto"
          hint="Só para você identificar na listagem. O contato não vê." />

        <Select
          label="Número de origem" name="phoneNumberId" required value={numero}
          placeholder={numeros.length ? 'Selecione' : 'Nenhum número conectado'}
          options={numeros.map((n) => ({ value: n.id, label: n.displayNumber }))}
          onChange={(e) => setNumero(e.currentTarget.value)}
        />

        <Select
          label="Segmento" name="tagId" value={etiqueta}
          placeholder="Todos os contatos com opt-in"
          options={listaTags.map((t) => ({ value: t.id, label: t.name }))}
          onChange={(e) => setEtiqueta(e.currentTarget.value)}
          hint="Em branco, atinge todos os contatos deste número que aceitaram mensagens promocionais."
        />

        <Select
          label="Modelo aprovado" name="templateId" required value={modelo}
          placeholder={numero ? 'Selecione' : 'Escolha o número primeiro'}
          options={modelosCompativeis.map((m) => ({ value: m.id, label: `${m.name} · ${m.language}` }))}
          onChange={(e) => setModelo(e.currentTarget.value)}
          hint={numero && modelosCompativeis.length === 0
            ? 'Nenhum modelo aprovado nesta conta WABA. Sincronize os modelos com a Meta em Modelos.'
            : 'A lista mostra apenas modelos aprovados da conta WABA deste número.'}
        />

        <Input
          label="Enviar em" name="scheduledAt" type="datetime-local" required
          min={minimo} defaultValue={minimo}
          hint="Precisa ser pelo menos 30 segundos no futuro. O horário é o deste navegador."
        />

        <Alcance numero={numero} calculando={calculando} alcance={alcance} />
      </form>
    </Drawer>
  );
}

function Alcance({
  numero, calculando, alcance,
}: {
  numero: string;
  calculando: boolean;
  alcance: { recipients: number; limitExceeded: boolean } | null;
}) {
  if (!numero) {
    return (
      <Notice tone="neutral" title="Alcance">
        Escolha o número para saber quantos contatos receberiam este disparo.
      </Notice>
    );
  }
  if (calculando) {
    return <Notice tone="neutral" title="Alcance"><span role="status">Calculando o alcance…</span></Notice>;
  }
  if (!alcance) {
    return (
      <Notice tone="warning" title="Alcance">
        Não foi possível calcular o alcance agora. O disparo ainda pode ser agendado — o servidor recalcula no envio.
      </Notice>
    );
  }
  if (alcance.recipients === 0) {
    return (
      <Notice tone="danger" title="Nenhum contato neste segmento" live>
        Nenhum contato deste número com consentimento promocional registrado pertence ao segmento escolhido.
        Registre o opt-in em Contatos antes de disparar.
      </Notice>
    );
  }
  return (
    <Notice tone={alcance.limitExceeded ? 'warning' : 'success'} title="Alcance" live>
      <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0 }}>
        <Users size={16} aria-hidden="true" />
        {alcance.recipients} {alcance.recipients === 1 ? 'contato receberia' : 'contatos receberiam'} este disparo.
      </p>
      {alcance.limitExceeded && (
        <p style={{ margin: 'var(--space-2) 0 0' }}>
          O segmento passa do teto de 500 destinatários e o servidor vai recusar. Restrinja por etiqueta.
        </p>
      )}
    </Notice>
  );
}
